import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { User, MapPin, Phone, Mail, ExternalLink, Building2, Globe, Target, Award, Factory, Eye, EyeOff } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/Auth/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { useLocation } from 'react-router-dom';
import { ProfileImageUpload } from './ProfileImageUpload';
import { useOrganization } from '@/hooks/useOrganization';
import { logSecurityEvent } from '@/utils/security';

// Allowed user roles
const allowedRoles = ['admin', 'data_entry', 'analyst', 'executive'] as const;
type UserRole = typeof allowedRoles[number];

interface UserProfile {
  id: string;
  user_id: string;
  role: UserRole;
  created_at: string;
  updated_at: string;
  full_name: string;
  job_title: string;
  department: string;
  phone: string;
  location: string;
  bio: string;
  linkedin_profile: string;
  expertise_areas: string[];
  profile_picture: string;
  storage_config: any;
  //organization_id: string;
  requires_storage_setup: boolean;
  storage_connected: boolean;
  
  // Organization Information
  company_name: string;
  company_website: string;
  company_size: 'small' | 'medium' | 'large' | 'enterprise' | '';
  industry_sector: string;
  founded_year: number | null;
  headquarters_location: string;
  
  // ESG Profile
  mission_statement: string;
  esg_policy: string;
  sustainability_statement: string;
  net_zero_target_year: number | null;
  carbon_neutral_target_year: number | null;
  sbti_commitment: boolean;
  sbti_validation_date: string;
  carbon_neutral_commitment: boolean;
  primary_sustainability_focus: string[];
  preferred_reporting_standards: string[];
  reporting_frequency: 'quarterly' | 'semi-annual' | 'annual' | '';
}


// Helper to format role for display
const formatRole = (role: string) => role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export const UserProfileSettings = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<UserProfile>({
    id: '',
    user_id: '',
    role: 'data_entry',
    created_at: '',
    updated_at: '',
    full_name: '',
    job_title: '',
    department: '',
    phone: '',
    location: '',
    bio: '',
    linkedin_profile: '',
    expertise_areas: [],
    profile_picture: '',
    storage_config: null,
    //organization_id: '',
    requires_storage_setup: true,
    storage_connected: false,
    
    // Organization Information
    company_name: '',
    company_website: '',
    company_size: '',
    industry_sector: '',
    founded_year: null,
    headquarters_location: '',
    
    // ESG Profile
    mission_statement: '',
    esg_policy: '',
    sustainability_statement: '',
    net_zero_target_year: null,
    carbon_neutral_target_year: null,
    sbti_commitment: false,
    sbti_validation_date: '',
    carbon_neutral_commitment: false,
    primary_sustainability_focus: [],
    preferred_reporting_standards: [],
    reporting_frequency: '',
  });
  const [newExpertise, setNewExpertise] = useState('');
  const [newSustainabilityFocus, setNewSustainabilityFocus] = useState('');
  const [newReportingStandard, setNewReportingStandard] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordVisibility, setPasswordVisibility] = useState({
    current: false,
    next: false,
    confirm: false,
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordStrength, setPasswordStrength] = useState({
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  const { organizationId, isOrganization, loading: orgLoading } = useOrganization();

  // Fetch profile on mount and when location changes to this tab
  useEffect(() => {
    if (user) {
      fetchProfile();
    }
    // eslint-disable-next-line
  }, [user, location.pathname, location.search]);

  useEffect(() => {
    const nextPassword = passwordForm.newPassword;
    if (!nextPassword) {
      setPasswordStrength({
        hasMinLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasNumber: false,
        hasSpecialChar: false,
      });
      return;
    }

    setPasswordStrength({
      hasMinLength: nextPassword.length >= 8,
      hasUpperCase: /[A-Z]/.test(nextPassword),
      hasLowerCase: /[a-z]/.test(nextPassword),
      hasNumber: /[0-9]/.test(nextPassword),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(nextPassword),
    });
  }, [passwordForm.newPassword]);

  const fetchProfile = async () => {
    if (!user) return;
    try {
      // Fetch from Supabase user_profiles table
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();
        
      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      
      if (data) {
        setProfile(prev => ({
          ...prev,
          ...data,
          expertise_areas: data.expertise_areas || [],
          // Organization fields
          company_name: data.company_name || '',
          company_website: data.company_website || '',
          company_size: data.company_size || '',
          industry_sector: data.industry_sector || '',
          founded_year: data.founded_year || null,
          headquarters_location: data.headquarters_location || '',
          // ESG fields
          mission_statement: data.mission_statement || '',
          esg_policy: data.esg_policy || '',
          sustainability_statement: data.sustainability_statement || '',
          net_zero_target_year: data.net_zero_target_year || null,
          carbon_neutral_target_year: data.carbon_neutral_target_year || null,
          sbti_commitment: data.sbti_commitment || false,
          sbti_validation_date: data.sbti_validation_date || '',
          carbon_neutral_commitment: data.carbon_neutral_commitment || false,
          primary_sustainability_focus: data.primary_sustainability_focus || [],
          preferred_reporting_standards: data.preferred_reporting_standards || [],
          reporting_frequency: data.reporting_frequency || '',
          // Storage config
          storage_config: data.storage_config || null,
        }));
      } else {
        // No profile exists yet, set up default with user ID
        setProfile(prev => ({
          ...prev,
          user_id: user.id,
        }));
      }
    } catch (error) {
      console.error('Error fetching profile from Supabase:', error);
      
      toast({
        title: "Error",
        description: "Failed to load profile. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSave = async () => {
    if (!user?.id) {
      toast({
        title: "Error",
        description: "User not authenticated. Please log in again.",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    try {
      // Prepare profile data for Supabase
      const profileData = {
        user_id: user.id,
        full_name: profile.full_name || '',
        job_title: profile.job_title || '',
        department: profile.department || '',
        phone: profile.phone || '',
        location: profile.location || '',
        bio: profile.bio || '',
        linkedin_profile: profile.linkedin_profile || '',
        expertise_areas: profile.expertise_areas || [],
        role: allowedRoles.includes(profile.role) ? profile.role : 'data_entry',
        profile_picture: profile.profile_picture || '',
        
        // Organization fields
        company_name: profile.company_name || null,
        company_website: profile.company_website || null,
        company_size: profile.company_size || null,
        industry_sector: profile.industry_sector || null,
        founded_year: profile.founded_year || null,
        headquarters_location: profile.headquarters_location || null,
        
        // ESG profile fields
        mission_statement: profile.mission_statement || '',
        esg_policy: profile.esg_policy || '',
        sustainability_statement: profile.sustainability_statement || '',
        net_zero_target_year: profile.net_zero_target_year,
        carbon_neutral_target_year: profile.carbon_neutral_target_year,
        sbti_commitment: profile.sbti_commitment || false,
        sbti_validation_date: profile.sbti_validation_date ||null,
        carbon_neutral_commitment: profile.carbon_neutral_commitment || false,
        primary_sustainability_focus: profile.primary_sustainability_focus || [],
        preferred_reporting_standards: profile.preferred_reporting_standards || [],
        reporting_frequency: profile.reporting_frequency || null,
        
        // Storage and other fields
        storage_config: profile.storage_config || {},
        //organization_id: profile.organization_id || null,
        requires_storage_setup: profile.requires_storage_setup !== undefined ? profile.requires_storage_setup : true,
        storage_connected: profile.storage_connected || false,
        updated_at: new Date().toISOString(),
      };
      
      // Save to Supabase user_profiles table
      const { data, error } = await supabase
        .from('user_profiles')
        .upsert({
          ...profileData,
          // Only include id if it exists (for update)
          ...(profile.id && { id: profile.id })
        }, {
          onConflict: 'user_id',
          ignoreDuplicates: false
        })
        .select()
        .single();
        
      if (error) throw error;
      
      // Update local state with the returned data
      if (data) {
        setProfile(prev => ({
          ...prev,
          ...data,
          id: data.id || prev.id
        }));
      }
      
      toast({
        title: "Success",
        description: "Profile updated successfully!",
      });
      
    } catch (error) {
      console.error('Error saving profile:', error);
      
      // Log detailed error information for debugging
      if (error && typeof error === 'object') {
        console.error('Error details:', {
          message: (error as any).message,
          code: (error as any).code,
          details: (error as any).details,
          hint: (error as any).hint,
          error: error
        });
      }
      
      let errorMessage = "Failed to save profile. Please try again.";
      if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = `Save failed: ${(error as any).message}`;
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const addExpertise = () => {
    if (newExpertise.trim() && !profile.expertise_areas.includes(newExpertise.trim())) {
      setProfile(prev => ({
        ...prev,
        expertise_areas: [...prev.expertise_areas, newExpertise.trim()]
      }));
      setNewExpertise('');
    }
  };

  const removeExpertise = (expertise: string) => {
    setProfile(prev => ({
      ...prev,
      expertise_areas: prev.expertise_areas.filter(e => e !== expertise)
    }));
  };

  const addSustainabilityFocus = () => {
    if (newSustainabilityFocus.trim() && !profile.primary_sustainability_focus.includes(newSustainabilityFocus.trim())) {
      setProfile(prev => ({
        ...prev,
        primary_sustainability_focus: [...prev.primary_sustainability_focus, newSustainabilityFocus.trim()]
      }));
      setNewSustainabilityFocus('');
    }
  };

  const removeSustainabilityFocus = (focus: string) => {
    setProfile(prev => ({
      ...prev,
      primary_sustainability_focus: prev.primary_sustainability_focus.filter(f => f !== focus)
    }));
  };

  const addReportingStandard = () => {
    if (newReportingStandard.trim() && !profile.preferred_reporting_standards.includes(newReportingStandard.trim())) {
      setProfile(prev => ({
        ...prev,
        preferred_reporting_standards: [...prev.preferred_reporting_standards, newReportingStandard.trim()]
      }));
      setNewReportingStandard('');
    }
  };

  const removeReportingStandard = (standard: string) => {
    setProfile(prev => ({
      ...prev,
      preferred_reporting_standards: prev.preferred_reporting_standards.filter(s => s !== standard)
    }));
  };

  const isPasswordStrong = () => Object.values(passwordStrength).every(Boolean);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user?.email) {
      toast({
        title: "Error",
        description: "Unable to verify your account email. Please sign in again.",
        variant: "destructive",
      });
      return;
    }

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast({
        title: "Missing Information",
        description: "Please fill in all password fields.",
        variant: "destructive",
      });
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast({
        title: "Password Mismatch",
        description: "New password and confirmation do not match.",
        variant: "destructive",
      });
      return;
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      toast({
        title: "Invalid Password",
        description: "Your new password must be different from your current password.",
        variant: "destructive",
      });
      return;
    }

    if (!isPasswordStrong()) {
      toast({
        title: "Weak Password",
        description: "Please meet all password requirements before saving.",
        variant: "destructive",
      });
      return;
    }

    setChangingPassword(true);
    try {
      const { error: reauthError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: passwordForm.currentPassword,
      });

      if (reauthError) {
        throw new Error("Current password is incorrect.");
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordForm.newPassword,
      });

      if (updateError) {
        throw new Error(updateError.message || "Failed to update password.");
      }

      await logSecurityEvent('password_change', 'User changed account password', {
        user_id: user.id,
        email: user.email,
      });

      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setPasswordVisibility({ current: false, next: false, confirm: false });

      toast({
        title: "Password Updated",
        description: "Your password has been changed successfully.",
      });
    } catch (error) {
      toast({
        title: "Password Change Failed",
        description: error instanceof Error ? error.message : "Unable to change password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Profile Information
            <span className="ml-2 text-xs text-gray-500">Role: {formatRole(profile.role)}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="full_name">Full Name</Label>
              <Input
                id="full_name"
                value={profile.full_name || ''}
                onChange={(e) => setProfile(prev => ({ ...prev, full_name: e.target.value }))}
                placeholder="Enter your full name"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                value={user?.email || ''}
                disabled
                className="bg-gray-50"
              />
            </div>
            <div>
              <Label htmlFor="job_title">Job Title</Label>
              <Input
                id="job_title"
                value={profile.job_title || ''}
                onChange={(e) => setProfile(prev => ({ ...prev, job_title: e.target.value }))}
                placeholder="e.g., Sustainability Manager"
              />
            </div>
            <div>
              <Label htmlFor="department">Department</Label>
              <Input
                id="department"
                value={profile.department || ''}
                onChange={(e) => setProfile(prev => ({ ...prev, department: e.target.value }))}
                placeholder="e.g., ESG"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="bio">Bio</Label>
            <Textarea
              id="bio"
              value={profile.bio}
              onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
              placeholder="Tell us about yourself..."
              rows={3}
            />
          </div>
          {/* Profile Picture Upload */}
          <div>
            <ProfileImageUpload
              currentImage={profile.profile_picture}
              onImageUpdate={img => setProfile(prev => ({ ...prev, profile_picture: img }))}
              userInitials={profile.full_name ? profile.full_name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Contact Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={profile.location}
                onChange={(e) => setProfile(prev => ({ ...prev, location: e.target.value }))}
                placeholder="City, Country"
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={profile.phone || ''}
                onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+1 (555) 000-0000"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="linkedin_profile">LinkedIn Profile</Label>
            <Input
              id="linkedin_profile"
              value={profile.linkedin_profile || ''}
              onChange={(e) => setProfile(prev => ({ ...prev, linkedin_profile: e.target.value }))}
              placeholder="https://linkedin.com/in/yourprofile"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Expertise Areas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              value={newExpertise}
              onChange={(e) => setNewExpertise(e.target.value)}
              placeholder="Add expertise area"
              onKeyPress={(e) => e.key === 'Enter' && addExpertise()}
            />
            <Button onClick={addExpertise} type="button">
              Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {profile.expertise_areas.map((expertise, index) => (
              <Badge
                key={index}
                variant="secondary"
                className="cursor-pointer"
                onClick={() => removeExpertise(expertise)}
              >
                {expertise} ×
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {isOrganization && !orgLoading &&
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Organization Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="company_name">Company/Organization Name</Label>
                <Input
                  id="company_name"
                  value={profile.company_name || ''}
                  onChange={(e) => setProfile(prev => ({ ...prev, company_name: e.target.value }))}
                  placeholder="Enter your company name"
                />
              </div>
              <div>
                <Label htmlFor="company_website">Company Website</Label>
                <Input
                  id="company_website"
                  type="url"
                  value={profile.company_website || ''}
                  onChange={(e) => setProfile(prev => ({ ...prev, company_website: e.target.value }))}
                  placeholder="https://www.company.com"
                />
              </div>
              <div>
                <Label htmlFor="company_size">Company Size</Label>
                <Select
                  value={profile.company_size || ''}
                  onValueChange={(value) => setProfile(prev => ({ ...prev, company_size: value as any }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select company size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Small (1-50 employees)</SelectItem>
                    <SelectItem value="medium">Medium (51-250 employees)</SelectItem>
                    <SelectItem value="large">Large (251-1000 employees)</SelectItem>
                    <SelectItem value="enterprise">Enterprise (1000+ employees)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="industry_sector">Industry Sector</Label>
                <Input
                  id="industry_sector"
                  value={profile.industry_sector || ''}
                  onChange={(e) => setProfile(prev => ({ ...prev, industry_sector: e.target.value }))}
                  placeholder="e.g., Technology, Manufacturing, Healthcare"
                />
              </div>
              <div>
                <Label htmlFor="founded_year">Founded Year</Label>
                <Input
                  id="founded_year"
                  type="number"
                  value={profile.founded_year || ''}
                  onChange={(e) => setProfile(prev => ({ ...prev, founded_year: parseInt(e.target.value) || null }))}
                  placeholder="e.g., 1990"
                />
              </div>
              <div>
                <Label htmlFor="headquarters_location">Headquarters Location</Label>
                <Input
                  id="headquarters_location"
                  value={profile.headquarters_location || ''}
                  onChange={(e) => setProfile(prev => ({ ...prev, headquarters_location: e.target.value }))}
                  placeholder="e.g., San Francisco, CA, USA"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      }

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            ESG Profile & Sustainability Commitments
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Mission & Statements */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold border-b pb-2">Mission & Statements</h3>
            <div>
              <Label htmlFor="mission_statement">Mission Statement</Label>
              <Textarea
                id="mission_statement"
                value={profile.mission_statement || ''}
                onChange={(e) => setProfile(prev => ({ ...prev, mission_statement: e.target.value }))}
                placeholder="Describe your organization's mission and purpose..."
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="esg_policy">ESG Policy</Label>
              <Textarea
                id="esg_policy"
                value={profile.esg_policy || ''}
                onChange={(e) => setProfile(prev => ({ ...prev, esg_policy: e.target.value }))}
                placeholder="Describe your ESG policy and commitment..."
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="sustainability_statement">Sustainability Statement</Label>
              <Textarea
                id="sustainability_statement"
                value={profile.sustainability_statement || ''}
                onChange={(e) => setProfile(prev => ({ ...prev, sustainability_statement: e.target.value }))}
                placeholder="Describe your sustainability vision and approach..."
                rows={3}
              />
            </div>
          </div>

          {/* Climate Commitments */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold border-b pb-2 flex items-center gap-2">
              <Target className="h-5 w-5" />
              Climate Commitments
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="net_zero_target_year">Net Zero Target Year</Label>
                <Input
                  id="net_zero_target_year"
                  type="number"
                  value={profile.net_zero_target_year || ''}
                  onChange={(e) => setProfile(prev => ({ ...prev, net_zero_target_year: parseInt(e.target.value) || null }))}
                  placeholder="e.g., 2050"
                />
              </div>
              <div>
                <Label htmlFor="carbon_neutral_target_year">Carbon Neutral Target Year</Label>
                <Input
                  id="carbon_neutral_target_year"
                  type="number"
                  value={profile.carbon_neutral_target_year || ''}
                  onChange={(e) => setProfile(prev => ({ ...prev, carbon_neutral_target_year: parseInt(e.target.value) || null }))}
                  placeholder="e.g., 2030"
                />
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="sbti_commitment"
                  checked={profile.sbti_commitment || false}
                  onCheckedChange={(checked) => setProfile(prev => ({ ...prev, sbti_commitment: !!checked }))}
                />
                <Label htmlFor="sbti_commitment" className="flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  Committed to Science Based Targets initiative (SBTi)
                </Label>
              </div>
              
              {profile.sbti_commitment && (
                <div>
                  <Label htmlFor="sbti_validation_date">SBTi Validation Date</Label>
                  <Input
                    id="sbti_validation_date"
                    type="date"
                    value={profile.sbti_validation_date || ''}
                    onChange={(e) => setProfile(prev => ({ ...prev, sbti_validation_date: e.target.value }))}
                  />
                </div>
              )}
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="carbon_neutral_commitment"
                  checked={profile.carbon_neutral_commitment || false}
                  onCheckedChange={(checked) => setProfile(prev => ({ ...prev, carbon_neutral_commitment: !!checked }))}
                />
                <Label htmlFor="carbon_neutral_commitment">Committed to Carbon Neutrality</Label>
              </div>
            </div>
          </div>

          {/* Sustainability Focus Areas */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold border-b pb-2">Primary Sustainability Focus Areas</h3>
            <div className="flex gap-2">
              <Input
                value={newSustainabilityFocus}
                onChange={(e) => setNewSustainabilityFocus(e.target.value)}
                placeholder="Add sustainability focus area"
                onKeyPress={(e) => e.key === 'Enter' && addSustainabilityFocus()}
              />
              <Button onClick={addSustainabilityFocus} type="button">
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {profile.primary_sustainability_focus.map((focus, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="cursor-pointer"
                  onClick={() => removeSustainabilityFocus(focus)}
                >
                  {focus} ×
                </Badge>
              ))}
            </div>
          </div>

          {/* Reporting Standards */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold border-b pb-2">Preferred Reporting Standards</h3>
            <div className="flex gap-2">
              <Input
                value={newReportingStandard}
                onChange={(e) => setNewReportingStandard(e.target.value)}
                placeholder="Add reporting standard (e.g., GRI, SASB, TCFD)"
                onKeyPress={(e) => e.key === 'Enter' && addReportingStandard()}
              />
              <Button onClick={addReportingStandard} type="button">
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {profile.preferred_reporting_standards.map((standard, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="cursor-pointer"
                  onClick={() => removeReportingStandard(standard)}
                >
                  {standard} ×
                </Badge>
              ))}
            </div>
          </div>

          {/* Reporting Frequency */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold border-b pb-2">Reporting Preferences</h3>
            <div>
              <Label htmlFor="reporting_frequency">Reporting Frequency</Label>
              <Select
                value={profile.reporting_frequency || ''}
                onValueChange={(value) => setProfile(prev => ({ ...prev, reporting_frequency: value as any }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select reporting frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="semi-annual">Semi-Annual</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Change Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label htmlFor="current_password">Current Password</Label>
              <div className="relative">
                <Input
                  id="current_password"
                  type={passwordVisibility.current ? 'text' : 'password'}
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                  placeholder="Enter current password"
                  autoComplete="current-password"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setPasswordVisibility(prev => ({ ...prev, current: !prev.current }))}
                >
                  {passwordVisibility.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div>
              <Label htmlFor="new_password">New Password</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={passwordVisibility.next ? 'text' : 'password'}
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                  placeholder="Create new password"
                  autoComplete="new-password"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setPasswordVisibility(prev => ({ ...prev, next: !prev.next }))}
                >
                  {passwordVisibility.next ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {passwordForm.newPassword && (
              <div className="space-y-1 text-xs">
                <p className="font-medium text-gray-700">Password requirements:</p>
                <p className={passwordStrength.hasMinLength ? 'text-green-600' : 'text-gray-600'}>At least 8 characters</p>
                <p className={passwordStrength.hasUpperCase ? 'text-green-600' : 'text-gray-600'}>One uppercase letter</p>
                <p className={passwordStrength.hasLowerCase ? 'text-green-600' : 'text-gray-600'}>One lowercase letter</p>
                <p className={passwordStrength.hasNumber ? 'text-green-600' : 'text-gray-600'}>One number</p>
                <p className={passwordStrength.hasSpecialChar ? 'text-green-600' : 'text-gray-600'}>One special character (!@#$%^&*)</p>
              </div>
            )}

            <div>
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  type={passwordVisibility.confirm ? 'text' : 'password'}
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setPasswordVisibility(prev => ({ ...prev, confirm: !prev.confirm }))}
                >
                  {passwordVisibility.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={
                changingPassword ||
                !passwordForm.currentPassword ||
                !passwordForm.newPassword ||
                !passwordForm.confirmPassword ||
                passwordForm.newPassword !== passwordForm.confirmPassword ||
                !isPasswordStrong()
              }
            >
              {changingPassword ? 'Changing Password...' : 'Change Password'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={loading}>
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};
